/* eslint-disable max-len */
/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { useLoader } from '../../../platform/Hook';
import { Spacer } from '../../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../../popups/Popup';
import { Button, Colors, Input, Prompt, Select, SelectItemAdapter } from './../../../ui';

const mediaDeviceAdapter: SelectItemAdapter<MediaDeviceInfo> = {
    key: (tm) => tm.deviceId,
    label: (tm) => tm.label,
};

function handleError(error) {
    console.log('navigator.MediaDevices.getUserMedia error: ', error.message, error.name);
}

const PreviewFrame = styled.div<{ ready: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 24rem;
    background: ${Colors.hover};
    padding: .5rem;
    border-radius: .125rem;

    video {
        position: relative;
        z-index: 1;
        min-height: 320px;
        min-width: 240px;
        max-height: 24rem;
        opacity: ${p => p.ready ? 1 : 0};
    }

    &:before {
        content: 'Video Preview';
        position: absolute;
        font-size: 2rem;
        font-weight: 450;
        opacity: .5;
    }
`;

const VideoPreview = React.memo<{ audioId?: string, videoId?: string }>((props) => {

    const [ready, setReady] = useState(false);
    const [player, setPlayer] = useState<HTMLVideoElement>();

    const stop = useCallback(() => {
        const stream = player?.srcObject as MediaStream;
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
    }, [player]);

    const start = useCallback(() => {
        stop();
        const constraints = {
            audio: { deviceId: props.audioId ? { exact: props.audioId } : undefined },
            video: { deviceId: props.videoId ? { exact: props.videoId } : undefined }
        };
        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
                player.srcObject = stream;
                setReady(true);
            })
            .catch(handleError);
    }, [stop, player, props]);

    useEffect(() => {
        if (!player) return;
        start();
        return () => stop();
    }, [start, stop, player]);

    return <PreviewFrame ready={ready}>
        <video autoPlay ref={setPlayer}></video>
    </PreviewFrame>;

});

export const callDevicePopup = (args: { audio?: string, video?: string }) => ({ ui: CallDevicePopup, args });

export const CallDevicePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {

    const [audio, setAudio] = useState(args.audio ?? null);
    const [video, setVideo] = useState(args.video ?? null);

    const [loading, devices] = useLoader(async () => {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        return await navigator.mediaDevices.enumerateDevices();
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!audio) setAudio(devices?.find(x => x.kind === 'audioinput')?.deviceId ?? null);
        if (!video) setVideo(devices?.find(x => x.kind === 'videoinput')?.deviceId ?? null);
    }, [devices]);

    const availableAudio = useMemo(() => devices?.filter(x => x.kind === 'audioinput') ?? [], [devices]);
    const availableVideo = useMemo(() => devices?.filter(x => x.kind === 'videoinput') ?? [], [devices]);

    return <Prompt ignoreEnter onAction={dismiss}>
        <Popup w={64}>
            <Popup.Close onAction={dismiss} />
            <Popup.Title>Call Setup</Popup.Title>
            <Popup.Content>
                {audio && video && <VideoPreview audioId={audio} videoId={video} />}
                <Spacer f={2} />
                <Input.Label style={{ marginBottom: '.25rem' }}>Audio:</Input.Label>
                <Select
                    adapter={mediaDeviceAdapter}
                    items={availableAudio}
                    selected={availableAudio.find(x => x.deviceId === audio)}
                    onChange={x => setAudio(x.deviceId)} />
                <Spacer f={2} />
                <Input.Label style={{ marginBottom: '.25rem' }}>Video:</Input.Label>
                <Select
                    adapter={mediaDeviceAdapter}
                    items={availableVideo}
                    selected={availableVideo.find(x => x.deviceId === video)}
                    onChange={x => setVideo(x.deviceId)} />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={!audio || !video} variant="primary" onAction={() => action({ audio, video })}>
                    Save
                </Button>
                <Button onAction={dismiss}>
                    Cancel
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};


